import { Grid, Tooltip } from '@components';
import { PageOptions, SortOptions } from '@components/shared';
import { createElement, stringifyParams } from '@utils';
import consumer from '../channels/consumer';
import ApplicationController from './application_controller';

export default class extends ApplicationController {
    static targets = [
        'gridContainer'
    ];

    declare gridContainerTarget: HTMLElement;

    static values = {
        confirmActionFormId: String,
        organizationId: {
            type: String,
            default: ''
        }
    };

    declare organizationIdValue: string;

    private grid: Grid<RunLog>;
    private parmsObj: ParamsObj = {};
    private disabled: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private runLogsUpdatesSubscription: any;

    connect(): void {
        this.parmsObj.page = {
            size: 30,
            number: 1
        };
        this.createGrid();
        if (this.organizationIdValue) {
            this.runLogsUpdatesSubscription = consumer.subscriptions.create(
                {
                    channel: 'RootRunLogsGridChannel',
                    organization_id: this.organizationIdValue,
                },
                {
                    received: this.handleRowUpdates,
                }
            );
        }
        // Listen for filter events from the jsonapi-filter component
        const filterComponent = document.querySelector('[data-controller="jsonapi-filter"]');
        if (filterComponent) {
            filterComponent.addEventListener("filter:apply", (event: any) => {
                this.parmsObj.filter = event.detail.filter;
                this.parmsObj.page.number = 1;
                this.reload();
            });
            filterComponent.addEventListener("filter:clear", () => {
                delete this.parmsObj.filter;
                this.parmsObj.page.number = 1;
                this.reload();
            });
            // Listen for filter:ready event to trigger initial reload
            filterComponent.addEventListener("filter:ready", (event: any) => {
                // Optionally, set initial filter params if provided
                if (event.detail && event.detail.filter) {
                    this.parmsObj.filter = event.detail.filter;
                }
                this.reload();
            });
        }
    }

    private handleRowUpdates = (update: RunLog) => {
        this.grid.updateRowData(update);
    };

    private createGrid() {
        this.grid = new Grid<RunLog>({
            key: 'id',
            allowSelection: false,
            showEmptyState: true,
            noRecordsTemplate: this.noRecordsTemplate,
            container: this.gridContainerTarget,
            columns: [
                {
                    headerTitle: 'Id',
                    field: 'id',
                    width: '100px',
                    sortable: true,
                    cellTemplate: (run_log) => {
                        return `
                            <div class="px-4 pr-6 py-2 sm:py-4 mr-auto sm:mr-0 flex items-center">
                                <span class="ow-anywhere min-w-24 pr-1"> 
                                    ${run_log.attributes.id}
                                </span>
                            </div>
                        `;
                    }
                },
                {
                    headerTitle: 'Status',
                    field: 'status',
                    sortable: false,
                    width: '110px',
                    cellTemplate: (data: RunLog) => this.createStatusCellTemplate(data)
                },
                {
                    headerTitle: 'Created At',
                    field: 'created_at',
                    width: '200px',
                    sortable: true,
                    cellTemplate: (run_log) => {
                        const date = new Date(run_log.attributes.created_at);
                        const options: Intl.DateTimeFormatOptions = {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                            hour12: true,
                            timeZoneName: 'long'
                        };
                        const formattedDate = new Intl.DateTimeFormat(undefined, options).format(date);
                        return `
                            <span class="ow-anywhere min-w-24 pr-1"> 
                                ${formattedDate}
                            </span>
                        `;
                    }
                },
                {
                    headerTitle: 'Event',
                    field: 'root_type',
                    sortable: true,
                    cellTemplate: (run_log) => {
                        const rootType = run_log.attributes.root_type
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(' ');
                        return `
                        <span class="ow-anywhere min-w-24 pr-1"> 
                            ${rootType}
                        </span>
                    `;
                    }
                },
                {
                    headerTitle: 'Name',
                    field: 'name',
                    sortable: true,
                    cellTemplate: (run_log) => {
                        const name = run_log.attributes.name
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(' ');
                        return `
                        <span class="ow-anywhere min-w-32"> 
                            ${name}
                        </span>
                    `;
                    }
                },
                {
                    headerTitle: 'Created By',
                    field: 'created_by',
                    sortable: false,
                    cellTemplate: (run_log) => {
                        const created_by = run_log.attributes.created_by
                            .split('_')
                            .map(word => word?.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(' ');
                        return `
                        <span class="ow-anywhere min-w-24 pr-1"> 
                            ${created_by}
                        </span>
                    `;
                    }
                },
                {
                    headerTitle: 'Tree',
                    field: 'tree_view_url',
                    width: '128px',
                    sortable: false,
                    cellTemplate: (run_log) => {
                        return `
                            <div><a href="${run_log.attributes.tree_view_url}" class="border p-1 border-bkblue text-bkblue rounded" target="_blank">View</a></div>
                        `;
                    }
                }
            ],
            pageOptions: {
                number: this.parmsObj.page.number,
                size: this.parmsObj.page.size,
            },
            onSort: (sort: SortOptions) => this.sort(sort),
            onPageChange: (page: PageOptions) => this.page(page),
        });
    }

    private noRecordsTemplate = () => {
        let message = 'No records to show.';
        return `<div class="h-96 flex justify-center items-center text-gray-600 px-4">
        <div class="text-center"> ${message} </div>`;
    };

    previous(e: Event): void {
        e.preventDefault();
        if (this.disabled) return;
        this.disableButtons();
        this.parmsObj.page.number = (+this.parmsObj.page.number || 0) - 1;
        this.reload();
    }

    next(e: Event): void {
        e.preventDefault();
        if (this.disabled) return;
        this.disableButtons();
        this.parmsObj.page.number = (+this.parmsObj.page.number || 0) + 1;
        this.reload();
    }

    disableButtons(): void {
        this.disabled = true;
    }

    enableButtons(): void {
        this.disabled = false;
    }

    reload(): void {
        const basePath = window.location.pathname;
        const query = stringifyParams(this.parmsObj);
        let url = basePath;
        if (query.length) {
            url = url + '?' + query;
            window.history.pushState({}, '', url);
        } else {
            window.history.pushState({}, '', basePath);
        }
        this.ajax({
            url,
            type: 'GET',
            dataType: 'json',
            success: (data) => {
                this.grid.pageOptions.size = this.parmsObj.page.size;
                this.grid.pageOptions.number = data.meta.pagination.current;
                this.grid.totalResultsCount = data.meta.pagination.records;
                this.grid.data = data.data;
                window.dispatchEvent(new Event('resize'));
            },
        });
    }

    createStatusCellTemplate(data: RunLog) {
        let template: string;
        template = '<div class="flex-1"></div>'
        const container = createElement(template);
        if (container) {
            Object.entries(data.attributes.status_counts_by_level).forEach(([level, counts]) => {
                const progressBar = this.createProgressBar(level, counts, data);
                container.appendChild(progressBar);
            });
        }
        return container;
    }

    createProgressBar(level: string, counts: StatusCounts, data: RunLog): HTMLDivElement {
        const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
        const progressBar = document.createElement('div');
        progressBar.className = 'progress-bar bg-white border border-bkblue';

        // Build popover content with admin_run_logs links for each status (no duplicate level)
        let formattedToolTipOutput = Object.entries(counts).map(([key, value]) => {
            const status = key;
            const rootId = (data && data.attributes && data.attributes.id) ? data.attributes.id : '';
            const url = `/admin_run_logs/${rootId}?status=${encodeURIComponent(status)}&level=${encodeURIComponent(level)}`;
            return `<a href="${url}" class="text-bkblue hover:text-bkblue-dark" target="_blank">${value} ${status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</a>`;
        }).join('<br>');

        // Use run_log_popover_controller instead of Tooltip
        progressBar.setAttribute('data-controller', 'run-log-popover');
        progressBar.setAttribute('data-run-log-popover-title-value', `Level: ${level}`);
        progressBar.setAttribute('data-run-log-popover-content-value', formattedToolTipOutput);
        progressBar.setAttribute('data-run-log-popover-placement-value', 'right');
        // Optionally, you can programmatically trigger show/hide if needed
        // progressBar.addEventListener('mouseenter', () => {/* show popover */});
        // progressBar.addEventListener('mouseleave', () => {/* hide popover */});

        Object.entries(counts).forEach(([status, count]) => {
            const percentage = (count / total) * 100;
            const segment = document.createElement('div');
            segment.className = `progress-segment ${status}`;
            segment.style.width = `${percentage}%`
            progressBar.appendChild(segment);

            if (Object.keys(counts).length == 1) {
                const label = document.createElement('span');
                label.className = 'progress-label';
                label.innerText = `${status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`;
                segment.appendChild(label);
            }
            else {
                const label = document.createElement('span');
                label.className = 'progress-label';
                label.innerText = `${count}`;
                segment.appendChild(label);
            }
        });

        return progressBar;
    }

    animateButton(event: Event): void {
        const button = event.currentTarget as HTMLElement;
        button.classList.add('pulse');
        setTimeout(() => {
            button.classList.remove('pulse');
        }, 500); // Match the duration of the pulse animation
    }

    pageSize(e: Event): void {
        this.parmsObj.page.number = 1;
        e.preventDefault();
        this.reload();
    }

    private sort(sort: SortOptions): void {
        this.parmsObj.sort = sort.direction == 'asc' ? sort.column : '-' + sort.column
        this.reload();
    }

    private page(page: PageOptions): void {
        this.parmsObj.page = {
            number: page.number,
            size: page.size
        };
        this.reload();
    }

    disconnect() {
        super.disconnect();
    }
}

interface ParamsPageObj {
    number?: number
    size?: number
}

interface ParamsObj {
    search?: string;
    filter?: object;
    status?: string;
    sort?: string;
    page?: ParamsPageObj;
}

interface RunLogAttributes {
    id: number;
    state: string;
    created_at: string;
    name: string;
    root_type: string;
    created_by: string;
    tree_view_url: string;
    status_counts_by_level: StatusCountsByLevel;
}

interface RunLog {
    attributes: RunLogAttributes;
}

interface StatusCounts {
    [status: string]: number;
}

interface StatusCountsByLevel {
    [level: string]: StatusCounts;
}
